/*
 * Presentation Templates — lazily loads a slide card's real photo(s),
 * extracted from its own source .pptx (see ../../shared/services/pptxSlideImageExtraction.ts).
 * Independent copy of slide-templates/hooks/useSlideTemplateThumbnail.ts's
 * pattern — this feature does not import from slide-templates.
 *
 * Fetches only when the card actually scrolls into view — a presentation can
 * hold dozens of slides across several source .pptx files, so eagerly
 * fetching every entry's file on mount would download the whole library
 * upfront. Two module-level caches keep repeat renders and same-file
 * siblings (several slides share one multi-slide .pptx) free:
 *   - rawPptxCache:    sourceFileUrl -> the downloaded bytes (fetched once)
 *   - slideImagesCache: `${sourceFileUrl}#${slideIndex}` -> that slide's
 *     extracted images (parsed once per slide, off the shared bytes)
 *
 * ONE CALLER PATH, TWO HOSTS
 * Both the in-pane Asset Library and the Office Dialog window fetch the
 * library the same way (see presentationTemplatesApi.ts's
 * `fetchPresentationTemplatesFromSasInfo`) and so both end up with real
 * `sourceFileUrl`/`slideIndex` on every slide — a SAS query string is
 * self-contained auth, so the dialog can `fetch()` a source `.pptx` directly
 * too, no pane round-trip needed. `loadThumbnail` below exists only as an
 * override point for a future host that genuinely can't fetch for itself;
 * neither current caller passes one. This hook only decides *when* to load
 * (on-visible) and holds the per-card result.
 */
import { useEffect, useRef, useState } from "react";
import { sasVersionHeaders } from "../../shared/services/blobImageUpload";
import { extractSlideImages, type ExtractedSlideImage } from "../../shared/services/pptxSlideImageExtraction";
import { logPresentationTemplatesError, type PresentationTemplateSlide } from "../services/presentationTemplatesApi";

const rawPptxCache = new Map<string, Promise<ArrayBuffer>>();
const slideImagesCache = new Map<string, Promise<readonly ExtractedSlideImage[]>>();

export const EMPTY_PRESENTATION_THUMBNAIL_IMAGES: readonly ExtractedSlideImage[] = [];

function fetchPptxBytes(sourceFileUrl: string): Promise<ArrayBuffer> {
  let cached = rawPptxCache.get(sourceFileUrl);
  if (!cached) {
    cached = fetch(sourceFileUrl, {
      method: "GET",
      headers: sasVersionHeaders(sourceFileUrl, logPresentationTemplatesError, "presentation-templates-thumbnail-sas-version-parse"),
    }).then((res) => {
      if (!res.ok) throw new Error(`Thumbnail source download failed (${res.status})`);
      return res.arrayBuffer();
    });
    cached.catch(() => rawPptxCache.delete(sourceFileUrl)); // don't pin a failed fetch forever
    rawPptxCache.set(sourceFileUrl, cached);
  }
  return cached;
}

/**
 * Fetches (with caching) `sourceFileUrl` and extracts `slideIndex`'s real
 * embedded picture(s). Exported so callers other than this hook's own
 * IntersectionObserver-gated effect (e.g. insert flows) can reuse the same
 * cache rather than re-downloading the same `.pptx`.
 */
export function loadPresentationTemplateSlideImages(
  sourceFileUrl: string,
  slideIndex: number,
): Promise<readonly ExtractedSlideImage[]> {
  const key = `${sourceFileUrl}#${slideIndex}`;
  let cached = slideImagesCache.get(key);
  if (!cached) {
    cached = fetchPptxBytes(sourceFileUrl).then((buffer) => extractSlideImages(buffer, slideIndex));
    cached.catch(() => slideImagesCache.delete(key));
    slideImagesCache.set(key, cached);
  }
  return cached;
}

/**
 * Returns the slide's real embedded photo(s), populated once `elementRef`
 * scrolls into the viewport. Empty (never null) until then, and stays empty
 * on failure — the caller's wireframe/placeholder fallback covers that case,
 * so this never surfaces its own error UI.
 *
 * @param loadThumbnail Dialog override — see the module header. Omit for the
 *   in-pane path, which fetches `slide.sourceFileUrl` directly.
 */
export function usePresentationTemplateThumbnail(
  slide: PresentationTemplateSlide,
  elementRef: React.RefObject<Element>,
  loadThumbnail?: (slide: PresentationTemplateSlide) => Promise<readonly ExtractedSlideImage[]>,
): readonly ExtractedSlideImage[] {
  const [images, setImages] = useState<readonly ExtractedSlideImage[]>(EMPTY_PRESENTATION_THUMBNAIL_IMAGES);
  const startedRef = useRef(false);
  const { id, sourceFileUrl, slideIndex, thumbnailUrl } = slide;

  useEffect(() => {
    startedRef.current = false;
    setImages(EMPTY_PRESENTATION_THUMBNAIL_IMAGES);
    // The aggregate presentations.cache manifest already supplies a real
    // rendered thumbnail (slide.thumbnailUrl) for most entries — fetching and
    // unzipping the whole source .pptx again for the same picture would be
    // pure waste, so only entries with no thumbnail of their own fall back to
    // extracting one from the .pptx.
    if (thumbnailUrl) return;
    // Without an override, this needs the direct-fetch fields the dialog's
    // stripped manifest copy never carries — nothing to do there.
    if (!loadThumbnail && (!sourceFileUrl || slideIndex === null)) return;

    const node = elementRef.current;
    if (!node) return;

    let cancelled = false;
    const observer = new IntersectionObserver(
      (entries) => {
        if (startedRef.current || !entries.some((entry) => entry.isIntersecting)) return;
        startedRef.current = true;
        observer.disconnect();
        const load = loadThumbnail ? loadThumbnail(slide) : loadPresentationTemplateSlideImages(sourceFileUrl!, slideIndex!);
        load
          .then((result) => {
            if (!cancelled) setImages(result);
          })
          .catch((error: unknown) => {
            logPresentationTemplatesError(
              "presentation-templates-thumbnail-extract",
              error instanceof Error ? error.message : "Thumbnail image extraction failed",
            );
          });
      },
      { rootMargin: "200px" },
    );
    observer.observe(node);
    return () => {
      cancelled = true;
      observer.disconnect();
    };
    // `slide` itself is intentionally omitted: id/sourceFileUrl/slideIndex/
    // thumbnailUrl are the only fields that can change what's loaded, and
    // depending on the object would re-run this on every parent re-render.
  }, [elementRef, id, sourceFileUrl, slideIndex, thumbnailUrl, loadThumbnail]);

  return images;
}
